import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Row } from "reactstrap";
import {
  getFormattedContactNumber,
  getFormattedOptions,
  getModifiedObject,
  Toast,
  uploadImage,
} from "utility/helpers";
import { fetchAllRegions } from "services/services.congressman";
import { fetchPartylists } from "services/services.affiliation";
import { genericPost, genericPut } from "services/services.general";
import { fetchCandidates } from "services/services.official";
import ImgContainer from "components/common/img";
import defaultUserImage from "assets/img/default_user.png";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import {
  FormContainer,
  FormHeaderSection,
  FormSection,
  HeaderText,
  ImageWrapper,
  UploadButton,
  UploadInput,
} from "assets/js/Styles";
import DirectoryForm from "../Form";
import { POSITION_VICE_MAYOR } from "../constants";
import { validateOfficialDirectoryForm } from "../senator/utility";

const EditViceMayor = () => {
  const location = useLocation();
  const viceMayor = location.state?.vicemayor || null;

  const [isLoading, setIsLoading] = useState(false);
  const [partylists, setPartylists] = useState([]);
  const [selectedPartylist, setSelectedPartylist] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const [regionOptions, setRegionOptions] = useState([]);

  const [viceMayorOptions, setViceMayorOptions] = useState([]);
  const [selectedViceMayor, setSelectedViceMayor] = useState(null);

  const [selectedRegion, setSelectedRegion] = useState(null);

  const [formFields, setFormFields] = useState({
    lastName: "",
    firstName: "",
    fullName: "",
    omegaId: "",
    region: "",
    contact: "",
    province: "",
    municipality: "",
    party: "",
    cos: "",
    cosNumber: "",
    photo: "",
    photoName: "",
    cosFirstName: "",
    cosLastName: "",
    name: "",
  });

  const navigate = useNavigate();

  const fetchViceMayors = async () => {
    await fetchCandidates({
      url: "/election/nominations/?position_id=3&is_winner=true&page_size=1700&is_active=true",
      callback: (data) => {
        if (data.results) {
          const rows = data.results;

          const viceMayors = rows.map((myr) => ({
            ...myr,
            value: myr.candidate.id,
            label: `${myr.candidate.first_name} ${myr.candidate.last_name}`,
          }));

          if (viceMayor) {
            const selectedViceMyr = viceMayors.find(
              (vicemyr) => vicemyr.candidate.id === viceMayor.omegaId
            );
            setSelectedViceMayor(selectedViceMyr);
          }

          setViceMayorOptions(viceMayors);
        }
      },
    });
  };

  const fetchRegion = () => {
    fetchAllRegions({
      callback: (data) => {
        if (data.length) {
          const options = data
            .filter((region) => region.name !== "Partylist")
            .map(({ acronym, id, omegaId }) => ({
              omegaId,
              label: acronym,
              value: id,
            }));

          setRegionOptions(options);

          const region = options?.find(
            (option) => option.value === viceMayor?.regionId
          );

          if (region) {
            setSelectedRegion(region);
          } else {
            setFormFields({
              ...formFields,
              region: null,
            });
          }
        }
      },
    });
  };

  const setData = () => {
    const option = partylists.find(
      (partylist) => partylist.id === viceMayor.partyId
    );

    if (option) setSelectedPartylist(option);

    setIsLoading(false);
  };

  const setViceMayorDetails = () => {
    const updatedFields = {
      ...formFields,
      lastName: viceMayor.lastName,
      firstName: viceMayor.firstName,
      fullName: viceMayor.fullName,
      contact: getFormattedContactNumber(viceMayor.contact),
      party: viceMayor.partyId,
      region: selectedRegion,
      provinceId: viceMayor.provinceId,
      municipalityId: viceMayor.municipalityId,
      cos: viceMayor.cosName,
      cosNumber: getFormattedContactNumber(viceMayor.cosContact),
      photo: viceMayor.photo || "",
      cosFirstName: viceMayor.cosFirstName || viceMayor.cosName,
      cosLastName: viceMayor.cosLastName,
    };

    if (viceMayor.photo) {
      const imgContainer = document.querySelector(".vicemayor-img");
      imgContainer.src = viceMayor.photo;
    }

    setFormFields(updatedFields);
  };

  const sendData = async (formData) => {
    setIsLoading(true);

    if (viceMayor) {
      await genericPut({
        url: `/official/${viceMayor.id}`,
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_VICE_MAYOR },
            });

            Toast({
              type: 1,
              content: "Successfully edited vice mayor details",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    } else {
      await genericPost({
        url: "/official",
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_VICE_MAYOR },
            });

            Toast({
              type: 1,
              content: "Successfully added vice mayor.",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    }
  };

  const handleOnSubmit = async () => {
    const isValid = await validateOfficialDirectoryForm(formFields);

    if (isValid.valid) {
      const data = {
        // eslint-disable-next-line max-len
        fullName: `VICE MAYOR ${formFields.firstName} ${formFields.lastName}`,
        firstName: formFields.firstName,
        lastName: formFields.lastName,
        contact: formFields.contact?.slice(1),
        regionId: formFields.region.value,
        provinceId: formFields.province.value,
        municipalityId: formFields.municipality.value,
        cosName: `${formFields.cosFirstName} ${formFields.cosLastName}`,
        cosFirstName: formFields.cosFirstName,
        cosLastName: formFields.cosLastName,
        omegaId: formFields.omegaId,
        position: POSITION_VICE_MAYOR,
        ...(formFields.cosNumber ? { cosContact: formFields.cosNumber } : {}),
        ...(selectedPartylist ? { partyId: selectedPartylist.value } : {}),
      };

      if (formFields.cosNumber) {
        data.cosContact = formFields.cosNumber?.slice(1);
      }

      let initialData = {};

      if (viceMayor) {
        initialData = {
          firstName: viceMayor.firstName,
          lastName: viceMayor.lastName,
          fullName: viceMayor.fullName,
          contact: viceMayor.contact,
          regionId: viceMayor.regionId,
          provinceId: viceMayor.provinceId,
          municipalityId: viceMayor.municipalityId,
          partyId: viceMayor.partyId,
          cosName: viceMayor.cosName,
          cosContact: viceMayor.cosContact,
          cosFirstName: viceMayor.cosFirstName,
          cosLastName: viceMayor.cosLastName,
          photo: viceMayor.photo,
          omegaId: formFields.omegaId,
          position: POSITION_VICE_MAYOR,
        };
      }

      setIsLoading(true);

      if (typeof formFields.photo !== "string") {
        const fileName = await uploadImage({
          photo: formFields.photo,
          photoName: formFields.photoName,
        });
        data.photo = fileName;
      }

      const formData = viceMayor
        ? await getModifiedObject(initialData, data)
        : data;

      await sendData(formData);
    } else {
      Toast({
        type: 4,
        content: isValid.error,
      });
    }
  };

  const fetchData = () => {
    fetchPartylists({
      callback: (data) => {
        const newData = getFormattedOptions("name", data);

        setPartylists(newData);
      },
    });

    setIsLoading(false);
  };

  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".vicemayor-img");

        const fileName = viceMayor
          ? `${
              viceMayor.id
            }-${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`
          : `${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`;

        setFormFields({
          ...formFields,
          photo: file,
          photoName: fileName,
        });

        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    //  fetch all district, partylist, region
    fetchRegion();
    fetchData();
    fetchViceMayors();
  }, []);

  useEffect(() => {
    if (viceMayor !== null) {
      setData();
      setViceMayorDetails();
      setIsEdit(true);
    }
  }, [partylists, viceMayor]);

  return (
    <FormContainer>
      <Col className="container">
        <FormHeaderSection>
          <HeaderText>
            {!viceMayor ? "Add Vice Mayor" : "Vice Mayor Details"}
          </HeaderText>
        </FormHeaderSection>

        <FormSection>
          <Row>
            <Col
              xs="12"
              sm="6"
              lg="4"
            >
              <ImageWrapper>
                <ImgContainer
                  alt="vicemayor-img"
                  className="vicemayor-img"
                  src={defaultUserImage}
                  width="196px"
                  height="196px"
                />

                <UploadButton htmlFor="file-upload">
                  {"Upload Photo"}
                  <UploadInput
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                </UploadButton>
              </ImageWrapper>
            </Col>
            <Col
              xs="12"
              sm="6"
              lg="8"
            >
              <DirectoryForm
                handleOnSubmit={handleOnSubmit}
                formFields={formFields}
                setFormFields={setFormFields}
                governorOptions={viceMayorOptions}
                selectedGovernor={selectedViceMayor}
                setSelectedGovernor={setSelectedViceMayor}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                regionOptions={regionOptions}
                partylists={partylists}
                selectedPartylist={selectedPartylist}
                setSelectedPartylist={setSelectedPartylist}
                isLoading={isLoading}
                isEdit={isEdit}
                position={POSITION_VICE_MAYOR}
              />
            </Col>
          </Row>
        </FormSection>
      </Col>
    </FormContainer>
  );
};

export default EditViceMayor;
